import { useState, useEffect, useCallback, useRef } from 'react';

export interface SrsNotification {
  id: string;
  type: 'join' | 'comment' | 'system';
  displayName: string;
  team: 'black' | 'white' | null;
  message?: string;
  timestamp: number;
}

const FONT_HUD  = "'Orbitron', monospace";
const FONT_BODY = "'Rajdhani', 'Noto Sans TC', system-ui, sans-serif";
const FONT_DECO = "'Cinzel', serif";

function teamStyle(team: 'black' | 'white' | null) {
  if (team === 'black') return { color: '#d4a843', rgb: '212,168,67', label: '黑隊' };
  if (team === 'white') return { color: '#e2e2e2', rgb: '226,226,226', label: '白隊' };
  return { color: '#a0a0a0', rgb: '160,160,160', label: null };
}

function getSystemStyle(message: string) {
  // 照片上傳回憶牆
  if (message.includes('上傳了') && message.includes('回憶牆'))
    return { color: '#e8a030', rgb: '232,160,48', icon: 'ri-image-2-fill', label: '新增回憶' };

  // 臨時任務（優先判斷，避免被後面條件截走）
  if (message.includes('臨時任務') && message.includes('已完成'))
    return { color: '#7ec8a0', rgb: '126,200,160', icon: 'ri-checkbox-circle-fill', label: '任務完成' };
  if (message.includes('臨時任務'))
    return { color: '#e05555', rgb: '224,85,85', icon: 'ri-alarm-warning-fill', label: '緊急任務' };

  // 遊戲開始（⚔️ or 遊戲正式開始）
  if (message.includes('⚔️') || message.includes('遊戲正式開始'))
    return { color: '#e05555', rgb: '224,85,85', icon: 'ri-sword-fill', label: '遊戲開始' };

  // 活動開始（🔥 先判斷，否則含「解鎖」的複合訊息會被截走）
  if (message.includes('開始了') || message.includes('🔥'))
    return { color: '#f09030', rgb: '240,144,48', icon: 'ri-fire-fill', label: '活動開始' };

  // 任務完成
  if (message.includes('已完成') || message.includes('✅'))
    return { color: '#7ec8a0', rgb: '126,200,160', icon: 'ri-checkbox-circle-fill', label: '任務完成' };

  // 任務解鎖 / 重新鎖定
  if (message.includes('解鎖') || message.includes('已解鎖'))
    return { color: '#d4a843', rgb: '212,168,67', icon: 'ri-lock-unlock-fill', label: '任務解鎖' };
  if (message.includes('🔒') || message.includes('重新鎖定'))
    return { color: '#888', rgb: '136,136,136', icon: 'ri-lock-fill', label: '任務鎖定' };

  // 評分
  if (message.includes('評分') || message.includes('⭐'))
    return { color: '#f0c040', rgb: '240,192,64', icon: 'ri-star-fill', label: '評分開放' };

  // 儀式
  if (message.includes('儀式') || message.includes('🎭'))
    return { color: '#c084fc', rgb: '192,132,252', icon: 'ri-spy-fill', label: '儀式開始' };

  // 積分
  if (message.includes('獲得') && (message.includes('+') || message.includes('-')))
    return { color: '#d4a843', rgb: '212,168,67', icon: 'ri-trophy-fill', label: '積分更新' };

  // 配對 / 小天使
  if (message.includes('配對') || message.includes('小主人'))
    return { color: '#c084fc', rgb: '192,132,252', icon: 'ri-spy-fill', label: '任務配對' };

  // 玩家加入
  if (message.includes('加入'))
    return { color: '#7ec8a0', rgb: '126,200,160', icon: 'ri-user-add-fill', label: '玩家加入' };

  // Fallback
  return { color: '#d4a843', rgb: '212,168,67', icon: 'ri-megaphone-fill', label: '系統通知' };
}

// ── Navigation helper ────────────────────────────────────────────────────────
function scrollToSection(id: string, offset = 80) {
  const el = document.getElementById(id);
  if (el) {
    window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - offset, behavior: 'smooth' });
  }
}

function extractQuoted(msg: string): string | null {
  const m = msg.match(/「(.+?)」/);
  return m ? m[1] : null;
}

function getNavigation(type: SrsNotification['type'], message?: string): (() => void) | null {
  if (type === 'join') return () => scrollToSection('members');
  if (type === 'comment') return () => scrollToSection('live-comments', 72);

  if (type === 'system' && message) {
    // 照片上傳 → 開啟回憶牆 Modal
    if (message.includes('上傳了') && message.includes('回憶牆')) {
      return () => window.dispatchEvent(new CustomEvent('srs-open-memory-wall'));
    }

    // 任務解鎖 / 任務重新鎖定
    if (message.includes('解鎖')) {
      return () => {
        scrollToSection('schedule');
        const title = extractQuoted(message);
        if (title) {
          setTimeout(() => window.dispatchEvent(new CustomEvent('srs-navigate-schedule', { detail: { taskTitle: title } })), 450);
        }
      };
    }
    if (message.includes('重新鎖定') || message.includes('已鎖定') || message.includes('🔒')) {
      return () => scrollToSection('schedule');
    }

    // 活動新增任務
    if (message.includes('新增了任務')) {
      return () => {
        scrollToSection('schedule');
        const title = extractQuoted(message);
        if (title) {
          setTimeout(() => window.dispatchEvent(new CustomEvent('srs-navigate-schedule', { detail: { taskTitle: title } })), 450);
        }
      };
    }
    // 臨時任務
    if (message.includes('臨時任務')) {
      return () => {
        const target = document.getElementById('schedule-custom-tasks') ?? document.getElementById('schedule');
        if (target) {
          window.scrollTo({ top: target.getBoundingClientRect().top + window.pageYOffset - 80, behavior: 'smooth' });
        }
        const title = extractQuoted(message);
        if (title) {
          setTimeout(() => window.dispatchEvent(new CustomEvent('srs-navigate-schedule', { detail: { taskTitle: title } })), 450);
        }
      };
    }
    // 積分
    if (message.includes('獲得') && (message.includes('+') || message.includes('-'))) {
      return () => scrollToSection('score');
    }
    // 活動開始 / 任務完成 / 已完成
    if (message.includes('開始了') || message.includes('已完成') || message.includes('🔥') || message.includes('✅')) {
      return () => scrollToSection('schedule');
    }
    // 評分
    if (message.includes('評分') || message.includes('⭐')) {
      return () => window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    // 儀式
    if (message.includes('儀式') || message.includes('🎭')) {
      return () => scrollToSection('overview');
    }
    // 配對啟動（秘密小天使）
    if (message.includes('配對已啟動') || message.includes('小主人')) {
      return () => scrollToSection('overview');
    }
    // 玩家加入
    if (message.includes('加入')) {
      return () => scrollToSection('members');
    }
    // 遊戲開始
    if (message.includes('遊戲正式開始') || message.includes('開始！')) {
      return () => scrollToSection('overview');
    }
    // 遊戲重置 / 清除
    if (message.includes('重置') || message.includes('清除')) {
      return () => window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // ── 通用 fallback：裁判手動廣播或未匹配的系統通知 → 跳到聊天留言板 ──
    return () => scrollToSection('live-comments', 72);
  }

  return null;
}

function ToastItem({
  notif,
  onDismiss,
  collapsed = false,
}: {
  notif: SrsNotification;
  onDismiss: (id: string) => void;
  collapsed?: boolean;
}) {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);

  const isSystem = notif.type === 'system';
  const isJoin = notif.type === 'join';

  const sysStyle = isSystem ? getSystemStyle(notif.message ?? '') : null;
  const { color, rgb, label } = isSystem
    ? { color: sysStyle!.color, rgb: sysStyle!.rgb, label: sysStyle!.label }
    : teamStyle(notif.team);

  const navFn = getNavigation(notif.type, notif.message);
  const isClickable = navFn !== null;

  useEffect(() => {
    const t1 = setTimeout(() => setVisible(true), 80);
    const duration = isSystem ? 6000 : 5000;
    const t2 = setTimeout(() => {
      setExiting(true);
      setTimeout(() => onDismiss(notif.id), 500);
    }, duration);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [notif.id, isSystem, onDismiss]);

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExiting(true);
    setTimeout(() => onDismiss(notif.id), 400);
  };

  const handleClick = () => {
    if (!isClickable) return;
    navFn();
    setExiting(true);
    setTimeout(() => onDismiss(notif.id), 400);
  };

  /* ── 折疊版（緊湊條狀）──────────────────────────────────────── */
  if (collapsed) {
    return (
      <div
        onClick={handleClick}
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        onKeyDown={isClickable ? e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); } } : undefined}
        className="relative overflow-hidden rounded-xl select-none w-full flex items-center gap-2 px-3"
        style={{
          height: 36,
          maxWidth: isSystem ? 340 : 320,
          cursor: isClickable ? 'pointer' : 'default',
          opacity: exiting ? 0 : visible ? 0.72 : 0,
          transform: exiting
            ? 'translateX(120%) scale(0.92)'
            : visible
            ? 'translateX(0) scale(1)'
            : 'translateX(60px) scale(0.94)',
          transition: exiting
            ? 'opacity 0.35s ease, transform 0.35s ease'
            : 'opacity 0.38s ease, transform 0.38s ease',
          willChange: 'transform, opacity',
          background: `linear-gradient(90deg, rgba(${rgb},0.12), rgba(${rgb},0.04))`,
          border: `1px solid rgba(${rgb},0.28)`,
          borderLeft: `3px solid rgba(${rgb},0.75)`,
          marginBottom: 6,
          WebkitTapHighlightColor: 'transparent',
          outline: 'none',
        }}
      >
        {/* Icon */}
        <i
          className={`${isSystem ? sysStyle!.icon : isJoin ? 'ri-sword-fill' : 'ri-chat-3-fill'} text-xs shrink-0`}
          style={{ color, filter: `drop-shadow(0 0 4px rgba(${rgb},0.7))` }}
        />
        {/* Label */}
        <span
          className="flex-1 min-w-0 truncate font-bold tracking-wider uppercase"
          style={{ fontSize: '9px', color: `rgba(${rgb},0.8)`, fontFamily: "'Cinzel', serif" }}
        >
          {isSystem ? label : isJoin ? 'JOINED' : 'BROADCAST'}
        </span>
        {/* Message preview */}
        {notif.message && (
          <span
            className="flex-1 truncate"
            style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', fontFamily: "'Rajdhani','Noto Sans TC',sans-serif", maxWidth: 140 }}
          >
            {notif.message}
          </span>
        )}
        {/* Navigate hint */}
        {isClickable && (
          <i className="ri-arrow-right-line shrink-0" style={{ fontSize: '10px', color: `rgba(${rgb},0.5)` }} />
        )}
        {/* Close */}
        <button
          onClick={handleDismiss}
          className="shrink-0 w-5 h-5 flex items-center justify-center rounded-md cursor-pointer"
          style={{ background: 'rgba(255,255,255,0.04)', color: '#444' }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = color; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = '#444'; }}
        >
          <i className="ri-close-line" style={{ fontSize: '10px' }} />
        </button>
      </div>
    );
  }

  /* ── 完整版 ──────────────────────────────────────────────────── */
  return (
    <div
      onClick={handleClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); } } : undefined}
      className="relative overflow-hidden rounded-2xl select-none w-full"
      style={{
        maxWidth: isSystem ? 340 : 320,
        cursor: isClickable ? 'pointer' : 'default',
        opacity: exiting ? 0 : visible ? 1 : 0,
        transform: exiting
          ? 'translateX(120%) scale(0.92)'
          : visible
          ? 'translateX(0) scale(1)'
          : 'translateX(60px) scale(0.94)',
        transition: exiting
          ? 'opacity 0.45s cubic-bezier(0.4, 0, 1, 1), transform 0.45s cubic-bezier(0.4, 0, 1, 1)'
          : 'opacity 0.42s cubic-bezier(0.34, 1.56, 0.64, 1), transform 0.42s cubic-bezier(0.34, 1.56, 0.64, 1)',
        willChange: 'transform, opacity',
        background: isSystem
          ? `linear-gradient(135deg, #0e0b04 0%, #130e02 60%, #0a0a0a 100%)`
          : 'linear-gradient(135deg, #080e1e 0%, #04091a 100%)',
        border: `1px solid rgba(${rgb},${isSystem ? '0.55' : '0.32'})`,
        boxShadow: isSystem
          ? `0 0 0 1px rgba(${rgb},0.15), 0 0 40px rgba(${rgb},0.30), 0 8px 50px rgba(0,0,0,0.9)`
          : `0 0 0 1px rgba(${rgb},0.08), 0 0 28px rgba(${rgb},0.18), 0 8px 40px rgba(0,0,0,0.85)`,
        marginBottom: 10,
        WebkitTapHighlightColor: 'transparent',
        outline: 'none',
      }}
    >
      {/* Hover overlay for clickable toasts */}
      {isClickable && (
        <div
          className="absolute inset-0 pointer-events-none rounded-2xl transition-opacity duration-200 opacity-0 hover:opacity-100"
          style={{ background: `rgba(${rgb},0.06)` }}
        />
      )}

      {/* Ambient top glow */}
      <div
        className="absolute top-0 left-0 right-0 pointer-events-none"
        style={{
          height: isSystem ? '60px' : '48px',
          background: `linear-gradient(180deg, rgba(${rgb},${isSystem ? '0.18' : '0.1'}), transparent)`,
        }}
      />

      {/* Top scan beam */}
      <div className="absolute top-0 left-0 right-0 h-px overflow-hidden">
        <div
          className="h-full"
          style={{
            background: `linear-gradient(90deg, transparent, rgba(${rgb},0.9), transparent)`,
            animation: 'ntfScan 1.8s linear infinite',
          }}
        />
      </div>

      {/* System: extra bottom glow line */}
      {isSystem && (
        <div className="absolute bottom-0 left-0 right-0 h-px"
          style={{ background: `linear-gradient(90deg, transparent, rgba(${rgb},0.6), transparent)` }} />
      )}

      {/* Progress bar at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5 overflow-hidden" style={{ background: `rgba(${rgb},0.10)` }}>
        <div
          className="h-full rounded-full"
          style={{
            background: `linear-gradient(90deg, rgba(${rgb},0.6), ${color})`,
            animation: `ntfBar ${isSystem ? 6 : 5}s linear forwards`,
            transformOrigin: 'left',
          }}
        />
      </div>

      {/* Corner brackets */}
      <div className="absolute top-2 left-2 pointer-events-none">
        <div className="w-3 h-3" style={{ borderLeft: `1.5px solid rgba(${rgb},0.7)`, borderTop: `1.5px solid rgba(${rgb},0.7)` }} />
      </div>
      <div className="absolute top-2 right-2 pointer-events-none">
        <div className="w-3 h-3" style={{ borderRight: `1.5px solid rgba(${rgb},0.7)`, borderTop: `1.5px solid rgba(${rgb},0.7)` }} />
      </div>

      {/* Content */}
      <div className="relative z-10 px-4 pt-3.5 pb-4">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="relative shrink-0 mt-0.5">
            {(isJoin || isSystem) && visible && (
              <div
                className="absolute inset-0 rounded-xl animate-ping opacity-30"
                style={{ background: `rgba(${rgb},0.5)` }}
              />
            )}
            <div
              className="relative flex items-center justify-center rounded-xl"
              style={{
                width: isSystem ? '40px' : '36px',
                height: isSystem ? '40px' : '36px',
                background: `linear-gradient(135deg, rgba(${rgb},0.28), rgba(${rgb},0.08))`,
                border: `1.5px solid rgba(${rgb},${isSystem ? '0.6' : '0.45'})`,
                boxShadow: `0 0 ${isSystem ? '18px' : '12px'} rgba(${rgb},${isSystem ? '0.5' : '0.35'})`,
              }}
            >
              <i
                className={`${isSystem ? sysStyle!.icon : isJoin ? 'ri-sword-fill' : 'ri-chat-3-fill'} ${isSystem ? 'text-base' : 'text-sm'}`}
                style={{ color, filter: `drop-shadow(0 0 6px rgba(${rgb},0.9))` }}
              />
            </div>
          </div>

          {/* Text */}
          <div className="flex-1 min-w-0">
            {/* Type label */}
            <div className="flex items-center gap-2 mb-1">
              <span
                className="font-black tracking-[0.35em] uppercase"
                style={{
                  fontSize: isSystem ? '10px' : '9px',
                  color: `rgba(${rgb},${isSystem ? '0.9' : '0.65'})`,
                  fontFamily: FONT_DECO,
                  textShadow: isSystem ? `0 0 8px rgba(${rgb},0.5)` : 'none',
                }}
              >
                {isSystem ? label : isJoin ? 'JOINED' : 'BROADCAST'}
              </span>
              {!isSystem && label && (
                <span
                  className="text-[9px] font-black px-1.5 py-0.5 rounded-full"
                  style={{
                    background: `rgba(${rgb},0.14)`,
                    color,
                    border: `1px solid rgba(${rgb},0.28)`,
                    fontFamily: FONT_BODY,
                  }}
                >
                  {label}
                </span>
              )}
            </div>

            {/* System message */}
            {isSystem ? (
              <p
                className="leading-relaxed font-semibold"
                style={{
                  fontSize: '0.8rem',
                  color: `rgba(${rgb},0.95)`,
                  fontFamily: FONT_BODY,
                  textShadow: `0 0 8px rgba(${rgb},0.25)`,
                }}
              >
                {notif.message}
              </p>
            ) : (
              <>
                {/* Name */}
                <div
                  className="font-black text-sm leading-tight"
                  style={{ color: '#f0f0f0', fontFamily: FONT_BODY, textShadow: `0 0 10px rgba(${rgb},0.3)` }}
                >
                  <span style={{ color }}>{notif.displayName}</span>
                  {isJoin ? (
                    <span className="text-[#888] font-normal ml-1 text-xs">
                      {' '}加入了{label ?? '戰場'}
                    </span>
                  ) : null}
                </div>

                {/* Message */}
                {!isJoin && notif.message && (
                  <p
                    className="text-xs mt-1 leading-relaxed truncate"
                    style={{
                      color: 'rgba(255,255,255,0.55)',
                      fontFamily: FONT_BODY,
                      maxWidth: 200,
                    }}
                    title={notif.message}
                  >
                    {notif.message}
                  </p>
                )}

                {isJoin && (
                  <p className="text-[10px] mt-1" style={{ color: '#444', fontFamily: FONT_BODY }}>
                    新的戰士已加入戰場
                  </p>
                )}
              </>
            )}
          </div>

          {/* Close */}
          <button
            onClick={handleDismiss}
            className="shrink-0 w-6 h-6 flex items-center justify-center rounded-lg cursor-pointer transition-all mt-0.5"
            style={{ background: 'rgba(255,255,255,0.04)', color: '#444' }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLElement).style.color = color;
              (e.currentTarget as HTMLElement).style.background = `rgba(${rgb},0.12)`;
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLElement).style.color = '#444';
              (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.04)';
            }}
          >
            <i className="ri-close-line text-xs" />
          </button>
        </div>

        {/* Footer tag */}
        <div className="flex items-center gap-1.5 mt-2.5">
          <span className="relative flex h-1.5 w-1.5 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60" style={{ background: isSystem ? color : '#7ec8a0' }} />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ background: isSystem ? color : '#7ec8a0' }} />
          </span>
          <span
            className="tracking-[0.3em] uppercase"
            style={{ fontSize: '9px', color: isSystem ? `rgba(${rgb},0.45)` : '#2a4a3a', fontFamily: FONT_HUD }}
          >
            SYSTEM ALERT
          </span>
          {/* Navigate hint */}
          {isClickable && (
            <span
              className="ml-auto flex items-center gap-1 font-bold"
              style={{ fontSize: '9px', color: `rgba(${rgb},0.55)`, fontFamily: FONT_BODY }}
            >
              點擊前往
              <i className="ri-arrow-right-line" style={{ fontSize: '9px' }} />
            </span>
          )}
          {!isClickable && (
            <span className="ml-auto tabular-nums" style={{ fontSize: '9px', color: '#2a2a2a', fontFamily: FONT_HUD }}>
              {(() => {
                const d = new Date(notif.timestamp);
                return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
              })()}
            </span>
          )}
        </div>
      </div>

      <style>{`
        @keyframes ntfScan {
          from { transform: translateX(-100%); }
          to   { transform: translateX(100%); }
        }
        @keyframes ntfBar {
          from { transform: scaleX(1); }
          to   { transform: scaleX(0); }
        }
      `}</style>
    </div>
  );
}

export default function NotificationToast() {
  const [queue, setQueue] = useState<SrsNotification[]>([]);
  const addedIds = useRef<Set<string>>(new Set());
  // 聊天通知靜音狀態（從 localStorage 初始化，並監聽 LiveScoreBoard 的切換事件）
  const chatMutedRef = useRef(localStorage.getItem('srs_chat_notif_muted') === '1');

  const dismiss = useCallback((id: string) => {
    setQueue(prev => prev.filter(n => n.id !== id));
  }, []);

  // 監聽靜音切換
  useEffect(() => {
    const handler = (e: Event) => {
      chatMutedRef.current = (e as CustomEvent<{ muted: boolean }>).detail?.muted ?? false;
    };
    window.addEventListener('srs-chat-notif-mute', handler);
    return () => window.removeEventListener('srs-chat-notif-mute', handler);
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const notif = (e as CustomEvent<SrsNotification>).detail;
      if (!notif?.id || addedIds.current.has(notif.id)) return;
      // 聊天靜音時，略過成員留言通知（type='comment'），系統通知照常顯示
      if (notif.type === 'comment' && chatMutedRef.current) return;
      addedIds.current.add(notif.id);
      setQueue(prev => [...prev, notif].slice(-5)); // max 5 stacked
    };
    window.addEventListener('srs-notification', handler);
    return () => window.removeEventListener('srs-notification', handler);
  }, []);

  if (queue.length === 0) return null;

  return (
    <div
      className="fixed top-16 right-2 sm:top-20 sm:right-4 z-[80] flex flex-col items-end pointer-events-none"
      style={{ maxWidth: 'calc(100vw - 16px)' }}
    >
      {queue.map((n, i) => (
        <div key={n.id} className="pointer-events-auto w-full">
          <ToastItem
            notif={n}
            onDismiss={dismiss}
            collapsed={queue.length > 3 && i < queue.length - 3}
          />
        </div>
      ))}
    </div>
  );
}